/*
  <predictive-search>: results as you type in the header's search panel, from the Predictive
  Search API (sections/predictive-search.liquid). The input is an ARIA combobox: arrow keys
  move through the results, Enter opens the highlighted one, Escape closes the list.
  Without JavaScript the same form opens the full search page.
*/
const Spectre = window.Spectre || {};

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.idle = this.querySelector('[data-predictive-idle]');
    this.status = this.querySelector('[data-predictive-status]');
    this.cache = new Map();
    if (!this.input || !this.results) return;
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  get options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  onInput = () => {
    window.clearTimeout(this.timer);
    const terms = this.input.value.trim();
    if (!terms) {
      this.close();
      return;
    }
    this.timer = window.setTimeout(() => this.search(terms), 220);
  };

  async search(terms) {
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      let html = this.cache.get(terms);
      if (!html) {
        const params = new URLSearchParams({
          q: terms,
          section_id: 'predictive-search',
          'resources[type]': 'query,product,collection,page',
          'resources[limit]': '4',
          'resources[limit_scope]': 'each',
        });
        const response = await fetch(`${Spectre.routes.predictiveSearch}?${params}`, { signal: this.abort.signal });
        if (!response.ok) throw new Error(String(response.status));
        const source = Spectre.parseHTML(await response.text()).querySelector('.predictive');
        html = source ? source.outerHTML : '';
        this.cache.set(terms, html);
      }
      if (this.input.value.trim() !== terms) return;
      this.results.innerHTML = html;
      this.open();
      const count = Number(this.results.querySelector('[data-predictive-count]')?.dataset.predictiveCount || 0);
      const message = count ? (Spectre.strings?.searchResults || '').replace('[count]', count) : '';
      if (this.status) this.status.textContent = message;
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    }
  }

  open() {
    this.results.hidden = false;
    if (this.idle) this.idle.hidden = true;
    this.input.setAttribute('aria-expanded', String(this.options.length > 0));
    this.setActive(null);
  }

  close() {
    this.results.hidden = true;
    this.results.replaceChildren();
    if (this.idle) this.idle.hidden = false;
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(null);
    if (this.status) this.status.textContent = '';
  }

  setActive(option) {
    this.options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
    if (option) {
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
    this.active = option;
  }

  onKeydown = (event) => {
    const options = this.options;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.results.hidden || !options.length) return;
      event.preventDefault();
      const index = options.indexOf(this.active);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = index === -1 ? (step === 1 ? 0 : options.length - 1) : (index + step + options.length) % options.length;
      this.setActive(options[next]);
    } else if (event.key === 'Enter' && this.active && !this.results.hidden) {
      event.preventDefault();
      this.active.click();
    } else if (event.key === 'Escape' && !this.results.hidden) {
      event.preventDefault();
      this.close();
    }
  };
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
